import ContactSearchComponent from 'components/app/support-desk/tickets-preview/ContactSearchComponent';
import Flex from 'components/common/Flex';
import Editor from 'components/editor/Editor';
import {
  DAYS_OF_MONTH,
  DAYS_OF_WEEK,
  REPORT_FREQUENCY
} from 'constants/customReportFields';
import { useCustomReportContext } from 'providers/CustomReportProvider';
import { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';

const SchedulingStep = () => {
  const { reportData, updateSchedule } = useCustomReportContext();
  const { schedule } = reportData;
  const [recipients, setRecipients] = useState('');

  const handleScheduleChange = (field, value) => {
    updateSchedule({
      [field]: value
    });
  };

  useEffect(() => {
    handleScheduleChange('recipients', recipients);
  }, [recipients]);

  const handleDaysChange = selectedOptions => {
    const selectedDays = selectedOptions
      ? selectedOptions
          .map(opt => opt.value)
          .sort((a, b) => a - b) // Sort in ascending order
          .join(',')
      : '';
    handleScheduleChange('daysOfWeek', selectedDays);
  };

  const handleDayOfMonthChange = selectedOption => {
    const dayOfMonth = selectedOption ? selectedOption.value : '';
    handleScheduleChange('dayOfMonth', dayOfMonth);
  };

  const getSelectedDays = () => {
    if (!schedule.daysOfWeek || typeof schedule.daysOfWeek !== 'string')
      return [];
    const selectedDayValues = schedule.daysOfWeek
      .split(',')
      .filter(Boolean)
      .map(Number);
    return DAYS_OF_WEEK.filter(day => selectedDayValues.includes(day.value));
  };

  const getSelectedDayOfMonth = () => {
    if (!schedule.dayOfMonth) return null;
    return DAYS_OF_MONTH.find(day => day.value === schedule.dayOfMonth);
  };

  return (
    <>
      <h5 className="mb-2">Agendamento</h5>
      <p className="text-muted mb-4 fs-11">
        Configure a execução automática e o envio por e-mail do seu relatório.
        Quando ativado, o relatório será gerado e enviado para os destinatários
        especificados.
      </p>

      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="enable-scheduling"
              label="Ativar Agendamento Automático"
              checked={schedule.enabled}
              onChange={e => handleScheduleChange('enabled', e.target.checked)}
            />
          </Form.Group>
        </Col>
      </Row>

      {schedule.enabled && (
        <Card className="mb-3">
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Frequência</Form.Label>
                  <Form.Select
                    value={schedule.frequency}
                    onChange={e =>
                      handleScheduleChange('frequency', e.target.value)
                    }
                  >
                    {REPORT_FREQUENCY.map(freq => (
                      <option key={freq.value} value={freq.value}>
                        {freq.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Repetir a cada</Form.Label>
                  <Form.Control
                    type="number"
                    value={schedule.executionInterval}
                    min={1}
                    required
                    onChange={e =>
                      handleScheduleChange('executionInterval', e.target.value)
                    }
                  />
                  <Form.Text className="text-muted">
                    {schedule.frequency === 'WEEKLY'
                      ? 'semana(s)'
                      : schedule.frequency === 'MONTHLY'
                      ? 'mês(es)'
                      : 'dia(s)'}
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Horário de Execução</Form.Label>
                  <Form.Control
                    type="time"
                    value={schedule.executionTime}
                    onChange={e =>
                      handleScheduleChange('executionTime', e.target.value)
                    }
                  />
                  <Form.Text className="text-muted">
                    O horário está no fuso horário local.
                  </Form.Text>
                </Form.Group>
              </Col>

              {schedule.frequency === 'WEEKLY' && (
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Dias da Semana</Form.Label>
                    <Select
                      isMulti
                      options={DAYS_OF_WEEK}
                      value={getSelectedDays()}
                      onChange={handleDaysChange}
                      placeholder="Selecione os dias..."
                      classNamePrefix="react-select"
                    />
                    <Form.Text className="text-muted">
                      Selecione um ou mais dias em que o relatório deve ser
                      executado
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}

              {schedule.frequency === 'MONTHLY' && (
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Dia do Mês</Form.Label>
                    <Select
                      options={DAYS_OF_MONTH}
                      value={getSelectedDayOfMonth()}
                      onChange={handleDayOfMonthChange}
                      placeholder="Selecione o dia do mês..."
                      classNamePrefix="react-select"
                    />
                    <Form.Text className="text-muted">
                      Selecione o dia do mês em que o relatório deve ser
                      executado
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}

              <Col md={12}>
                <hr className="my-3" />
                <h6 className="mb-3">Configuração de E-mail</h6>
              </Col>

              <Col md={12}>
                <Form.Group className="mb-3">
                  <ContactSearchComponent
                    label="Destinatários"
                    required={schedule.enabled}
                    onChange={setRecipients}
                    value={schedule.recipients}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Assunto do E-mail <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Relatório Personalizado - {NomeDoRelatorio}"
                    value={schedule.emailSubjectTemplate}
                    onChange={e =>
                      handleScheduleChange(
                        'emailSubjectTemplate',
                        e.target.value
                      )
                    }
                    required
                  />
                  <Form.Text className="text-muted">
                    Você pode usar {'{NomeDoRelatorio}'} como variável.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Corpo do E-mail <span className="text-danger">*</span>
                  </Form.Label>

                  <Editor
                    data={schedule.emailBodyTemplate}
                    value={value =>
                      handleScheduleChange('emailBodyTemplate', value)
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Formato do Anexo <span className="text-danger">*</span>
                  </Form.Label>
                  <Flex alignItems="center" gap={2}>
                    <Form.Check
                      type="checkbox"
                      name="attachmentFormat"
                      id="attachmentFormatExcel"
                      label="Excel"
                      value="EXCEL"
                      checked={schedule.sendAsExcel}
                      className="me-2"
                      onChange={e =>
                        handleScheduleChange(
                          'sendAsExcel',
                          !schedule.sendAsExcel
                        )
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      name="attachmentFormat"
                      id="attachmentFormatPDF"
                      label="PDF"
                      value="PDF"
                      checked={schedule.sendAsPdf}
                      className="me-2"
                      onChange={e =>
                        handleScheduleChange('sendAsPdf', !schedule.sendAsPdf)
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      name="attachmentFormat"
                      className="me-2"
                      id="attachmentFormatCSV"
                      label="CSV"
                      value="CSV"
                      checked={schedule.sendAsCsv}
                      onChange={e =>
                        handleScheduleChange('sendAsCsv', !schedule.sendAsCsv)
                      }
                    />
                  </Flex>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default SchedulingStep;
